import { useRef, useCallback } from 'react';
import './PersonImageLayer.css';

const PersonImageLayer = ({
  personImage,
  position = { x: 0, y: 0 },
  scale = 1.0,
  backgroundRemoved = false,
  isVirtualTryOnResult = false,
  onImageClick,
  onBackgroundClick,
  canvasWidth,
  canvasHeight,
  showPreviewBadge = false,
  className = ''
}) => {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  // Check if click is on a non-transparent pixel
  const isClickOnOpaquePixel = useCallback((e) => {
    const img = imgRef.current;
    if (!img) return true; // Fallback to treating as opaque

    try {
      // Create a temporary canvas to read pixel data
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      // Set canvas size to match image natural size
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      // Draw image to canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      // Get click position relative to the image element
      const rect = img.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Scale click position to natural image coordinates
      const scaleX = img.naturalWidth / rect.width;
      const scaleY = img.naturalHeight / rect.height;
      const naturalX = Math.floor(clickX * scaleX);
      const naturalY = Math.floor(clickY * scaleY);

      // Check bounds
      if (naturalX < 0 || naturalX >= canvas.width || naturalY < 0 || naturalY >= canvas.height) {
        return false;
      }

      // Get pixel data at click position
      const pixelData = ctx.getImageData(naturalX, naturalY, 1, 1).data;
      const alpha = pixelData[3]; // Alpha channel

      // Consider pixel opaque if alpha > 10 (small threshold for anti-aliasing)
      return alpha > 10;
    } catch (error) {
      // If CORS or other error, fallback to treating as opaque
      console.warn('Could not check pixel transparency:', error);
      return true;
    }
  }, []);

  const handleClick = useCallback((e) => {
    e.stopPropagation(); // Prevent background click

    if (isClickOnOpaquePixel(e)) {
      // Click on person - open person modal
      if (onImageClick) {
        onImageClick(e);
      }
    } else {
      // Click on transparent area - open background modal
      if (onBackgroundClick) {
        onBackgroundClick(e);
      }
    }
  }, [isClickOnOpaquePixel, onImageClick, onBackgroundClick]);

  if (!personImage) {
    return null;
  }

  // Ensure we have valid canvas dimensions
  const validCanvasWidth = canvasWidth || 400; // Fallback width
  const validCanvasHeight = canvasHeight || 600; // Fallback height

  // Calculate proper scaling to ensure person image is 80% of canvas height and centered
  const targetHeight = validCanvasHeight * 0.8; // 80% of canvas height
  
  // Use a more direct approach with CSS for better centering and sizing
  const imageStyle = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)', // Perfect centering
    height: `${Math.max(targetHeight, 300)}px`, // Minimum 300px height
    width: 'auto', // Maintain aspect ratio - CRITICAL for aspect ratio preservation
    maxWidth: `${Math.max(validCanvasWidth * 0.6, 200)}px`, // Minimum 200px width
    maxHeight: `${validCanvasHeight}px`, // Ensure it doesn't exceed canvas height
    objectFit: 'contain', // Maintain aspect ratio
    objectPosition: 'center', // Center the image
    cursor: onImageClick ? 'pointer' : 'default',
    zIndex: 1, // Below clothing items but above background
    opacity: 1, // Always fully opaque
    transition: 'opacity 0.3s ease, transform 0.2s ease',
    display: 'block', // Ensure it's displayed
    visibility: 'visible', // Force visibility
    imageRendering: 'auto' // Let browser handle quality
  };

  // Add specific styling for background-removed images
  if (backgroundRemoved) {
    imageStyle.backgroundColor = 'transparent';
  }

  // Add visual feedback for virtual try-on results
  const containerClassName = `person-image-layer ${className} ${isVirtualTryOnResult ? 'virtual-result' : 'manual-mode'
    } ${backgroundRemoved ? 'background-removed' : ''}`.trim();

  console.log('🖼️ PersonImageLayer render:', {
    personImage: personImage?.substring(0, 50),
    isVirtualTryOnResult,
    timestamp: Date.now()
  });

  return (
    <div className={containerClassName}>
      <img
        ref={imgRef}
        src={personImage}
        alt="Person for virtual try-on"
        style={imageStyle}
        onClick={handleClick}
        crossOrigin="anonymous"
        onLoad={() => {
          console.log('✅ Person image loaded:', personImage?.substring(0, 50));
        }}
        onError={() => {
          console.error('❌ Person image failed to load:', personImage?.substring(0, 50));
        }}
        draggable={false} // Prevent default drag behavior
      />

      {showPreviewBadge && (
        <div style={{
          position: 'absolute', top: '12px', right: '12px',
          background: 'rgba(0,0,0,0.6)', color: 'white',
          padding: '4px 10px', borderRadius: '999px',
          fontSize: '11px', fontWeight: 600,
          letterSpacing: '0.02em',
          zIndex: 10, pointerEvents: 'none'
        }}>
          Preview
        </div>
      )}

    </div>
  );
};

export default PersonImageLayer;